import * as React from 'react';
import propz from 'propz';
import { Field, Form, Formik } from 'formik';
import { Tournament } from 'Src/models/tournament';
import { Button } from 'Src/components/Button/Button';
import { AppUser } from 'Src/views/App/App';
import { AGE_GROUPS } from 'Src/consts/school';
import { GENDER, SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import {
  isIndividualTournament,
  isMiniTeamTournament,
  isSingleDoubleTournament,
  isTeamTournament
} from 'Src/helpers/tournament/tournament';
import { Switch } from 'Src/components/Switch/Switch';
import { INDIVIDUAL_TOURNAMENT_MAXIMUM_NUMBER_TEAMS_SCHOOL } from '../../../../../consts/tournament';
import '../../../AdminView/ClubProgrammes/ClubProgrammes.css';

interface Props {
  user: AppUser;
  tournament: Tournament;
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

export const TournamentAutoEnrollmentForm: React.FunctionComponent<Props> = props => {
  const { tournament, onSubmit, user } = props;

  const tournamentAgesGroupName = propz.get(user, ['activeSchool', 'ageGroupsNaming'], []) as any;

  const getAutoEnrollmentTeamsSettingsFormFields = () => {
    const isMixedTeamsAvailable = propz.get(tournament, ['isMixedTeamsAvailable']);
    const ages = propz.get(tournament, ['ages'], []);
    const gender = propz.get(tournament, ['gender'], GENDER.MIXED);

    const isGenderMixed = gender === GENDER.MIXED;
    const isGenderMale = gender === GENDER.MALE_ONLY;
    const isGenderFemale = gender === GENDER.FEMALE_ONLY;

    let autoEnrollmentSettingByAgeAndGender = [];

    switch (true) {
      case isGenderMixed && !isMixedTeamsAvailable:
        ages.forEach(age => {
          autoEnrollmentSettingByAgeAndGender.push(getAutoEnrollmentSettingByAgeAndGender(age, GENDER.MALE_ONLY));
          autoEnrollmentSettingByAgeAndGender.push(getAutoEnrollmentSettingByAgeAndGender(age, GENDER.FEMALE_ONLY));
        });
        break;
      case isGenderMixed && isMixedTeamsAvailable:
        ages.forEach(age => {
          autoEnrollmentSettingByAgeAndGender.push(getAutoEnrollmentSettingByAgeAndGender(age, GENDER.MIXED));
        });
        break;
      case isGenderMale:
        ages.forEach(age => {
          autoEnrollmentSettingByAgeAndGender.push(getAutoEnrollmentSettingByAgeAndGender(age, gender));
        });
        break;
      case isGenderFemale:
        ages.forEach(age => {
          autoEnrollmentSettingByAgeAndGender.push(getAutoEnrollmentSettingByAgeAndGender(age, gender));
        });
        break;
    }
    return autoEnrollmentSettingByAgeAndGender;
  };

  const getAutoEnrollmentSettingByAgeAndGender = (age: number, gender: string) => {
    const autoEnrollmentTeamsSettings = propz.get(tournament, ['autoEnrollmentTeamsSettings'], []);

    const setting = autoEnrollmentTeamsSettings.find(autoEnrollmentTeamsSetting => {
      const isAgeEqual = autoEnrollmentTeamsSetting.age === age;
      const isGenderEqual = autoEnrollmentTeamsSetting.gender === gender;

      return isAgeEqual && isGenderEqual;
    });

    const maximumNumberTeams = propz.get(setting, ['maximumNumberTeams'], 0);

    return {
      maximumNumberTeams,
      age,
      gender
    };
  };

  const onIsMaximumNumberTeamsChange = (e, index, values) => {
    const autoEnrollmentTeamsSettings = [...values.autoEnrollmentTeamsSettings];
    autoEnrollmentTeamsSettings[index].maximumNumberTeams = Number(e.target.value);
    return autoEnrollmentTeamsSettings;
  };

  const {
    name: tournamentName,
    maximumNumberTeamsOfSchool,
    isReadOnly,
    isAutoEnrollmentTeams,
    isEnableWaitingListWhenFull,
    isAutoEnrollmentToWaitingList
  } = tournament;

  const isTeamSingleDoubleOrMiniTeamTournament =
    isTeamTournament(tournament) || isSingleDoubleTournament(tournament) || isMiniTeamTournament(tournament);

  const autoEnrollmentForm = {
    autoEnrollmentTeamsSettings: getAutoEnrollmentTeamsSettingsFormFields() || [],
    maximumNumberTeamsOfSchool: isIndividualTournament(tournament)
      ? INDIVIDUAL_TOURNAMENT_MAXIMUM_NUMBER_TEAMS_SCHOOL
      : maximumNumberTeamsOfSchool,
    isReadOnly: isReadOnly,
    isAutoEnrollmentTeams: isAutoEnrollmentTeams,
    isEnableWaitingListWhenFull:
      tournament.isEnableWaitingListWhenFull !== undefined
        ? tournament.isEnableWaitingListWhenFull
        : isAutoEnrollmentTeams
        ? true
        : false,
    isAutoEnrollmentToWaitingList: isAutoEnrollmentToWaitingList,
    autoEnrollmentSettings: propz.get(tournament, ['autoEnrollmentTeamsSettings', 0, 'maximumNumberTeams'], 0)
  };

  return (
    <Formik
      initialValues={autoEnrollmentForm}
      onSubmit={values => {
        onSubmit(values);
      }}
      >
      {({ values, setFieldValue }) => (
        <Form className="eClubProgrammeForm tAeForm">
          <div className="eCpfBody">
            <div className="tAeSwitchGrid">
              <Field
                name="isReadOnly"
              >
                {({ field }) => (
                  <Switch {...field} text={'Schools in read-only mode'} customClass="tAeSwitchItem" />
                )}
              </Field>

              <Field
                name="isAutoEnrollmentTeams"
              >
                {({ field }) => (
                  <Switch
                    {...field}
                    onChange={event => {
                      const value = event.target.checked;
                      if (!value) {
                        setFieldValue('isAutoEnrollmentToWaitingList', value);
                      }
                      setFieldValue('isAutoEnrollmentTeams', value);
                    }}
                    text={'Autoenrollment'}
                    customClass="tAeSwitchItem"
                  />
                )}
              </Field>

              <Field
                name="isEnableWaitingListWhenFull"
              >
                {({ field }) => (
                  <Switch
                    {...field}
                    disabled={!values.isAutoEnrollmentTeams}
                    text={'Move tournament to waiting list mode once full'}
                    customClass="tAeSwitchItem"
                  />
                )}
              </Field>

              <Field
                name="isAutoEnrollmentToWaitingList"
              >
                {({ field }) => (
                  <Switch
                    {...field}
                    disabled={!values.isAutoEnrollmentTeams}
                    text={'Enrollment onto the waiting list only'}
                    customClass="tAeSwitchItem"
                  />
                )}
              </Field>
            </div>

            {isIndividualTournament(tournament) && (
              <>
                <div>Specify the maximum number of teams taking part in {tournamentName}:</div>

                <div key={'autoEnrollmentSettings'} className="form-group">
                  <div className="form-group">
                    <label>Maximum number of teams</label>
                    <Field type="number" min="0" name="autoEnrollmentSettings" className="form-control" />
                  </div>
                </div>
              </>
            )}

            {isTeamSingleDoubleOrMiniTeamTournament &&
              values.autoEnrollmentTeamsSettings.map(({ age, gender, maximumNumberTeams }, index) => {
                return (
                  <div key={'autoEnrollmentTeamsSettings' + index} className="eCpfField">
                    <label className="eCpfLabel">Maximum total number of teams for {tournamentName}</label>
                    <div className="eCpfHint">Use 0 for unlimited.</div>
                    <div className="eCpfInputWrap">
                      <Field
                        type="number"
                        min="0"
                        value={maximumNumberTeams}
                        onChange={event => {
                          setFieldValue(
                            'autoEnrollmentTeamsSettings',
                            onIsMaximumNumberTeamsChange(event, index, values)
                          );
                        }}
                        className="eCpfInput"
                      />
                    </div>
                  </div>
                );
              })}

            {isTeamSingleDoubleOrMiniTeamTournament && (
              <div className="eCpfField">
                <label className="eCpfLabel">Maximum number of teams one school can provide</label>
                <div className="eCpfHint">Use 0 for unlimited. Otherwise enter a value from 1 to 5.</div>
                <div className="eCpfInputWrap">
                  <Field type="number" name="maximumNumberTeamsOfSchool" min="0" max="5" className="eCpfInput" />
                </div>
              </div>
            )}
          </div>

          <div className="eCpfFooter tAeFooter">
            <Button text="Cancel" customClass="btn btn-light border" onClick={props.onCancel} />
            <button type="submit" className="btn btn-primary">
              Save
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

TournamentAutoEnrollmentForm.displayName = 'TournamentAutoEnrollmentForm';

